import { Button as AntButton, Space, Spin } from 'antd'
import { CopyableValue } from 'components/CopyableValue'
import { getNoyoPlatformEmployee } from 'enrollment-platform/api/partner-benefit-graph/noyo-differences.client'
import type {
  NoyoDifferenceField,
  NoyoPlanMember,
} from 'enrollment-platform/api/partner-benefit-graph/noyo-differences.types'
import { getErrorMessage } from '../IdeonConfig/hooks/getErrorMessage'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { employeeAdminPath } from './issueLinks'
import { memberNameFromFields } from './memberName'

const TerminatedMark = styled.span`
  color: #cf1322;
  font-weight: 700;
`

type PlatformEmployee = {
  employeePublicKey: string
  name: string
  isNoyoTerminated: boolean
  surecoName: string
  isSurecoTerminated: boolean
}

const platformEmployeeCache = new Map<string, Promise<PlatformEmployee>>()

function fetchPlatformEmployee(
  noyoEmployeeId: string
): Promise<PlatformEmployee> {
  const cached = platformEmployeeCache.get(noyoEmployeeId)
  if (cached) {
    return cached
  }
  const request = getNoyoPlatformEmployee(noyoEmployeeId).catch((error) => {
    platformEmployeeCache.delete(noyoEmployeeId)
    throw error
  })
  platformEmployeeCache.set(noyoEmployeeId, request)
  return request
}

export function clearMemberNameCache() {
  platformEmployeeCache.clear()
}

type MemberNameCellProps = {
  side: 'sureco' | 'noyo'
  noyoEmployeeId: string
  fields: NoyoDifferenceField[] | undefined
  fetchedMembers?: NoyoPlanMember[]
  fetching?: boolean
}

type LoadedMember = {
  name: string
  isTerminated: boolean
  employeePublicKey: string
}

function MemberRow({
  member,
  canGotoEmployee,
  loading,
  onGotoEmployee,
}: {
  member: { name: string; isTerminated: boolean; employeePublicKey?: string }
  canGotoEmployee: boolean
  loading: boolean
  onGotoEmployee: () => void
}) {
  const terminateMark = member.isTerminated ? (
    <TerminatedMark> (Terminate)</TerminatedMark>
  ) : null

  if (canGotoEmployee) {
    return (
      <Space>
        <AntButton loading={loading} onClick={onGotoEmployee}>
          {member.name}
          {terminateMark}
        </AntButton>
        <CopyableValue value={member.name} iconOnly />
      </Space>
    )
  }

  return (
    <Space>
      <span>
        {member.name}
        {terminateMark}
      </span>
      <CopyableValue value={member.name} iconOnly />
    </Space>
  )
}

function loadedMemberFromResult(
  side: 'sureco' | 'noyo',
  result: PlatformEmployee
): LoadedMember | null {
  if (side === 'sureco') {
    const name = result.surecoName.length > 0 ? result.surecoName : result.name
    if (name.length === 0) {
      return null
    }
    return {
      name,
      isTerminated: result.isSurecoTerminated,
      employeePublicKey: result.employeePublicKey,
    }
  }
  if (result.name.length === 0) {
    return null
  }
  return {
    name: result.name,
    isTerminated: result.isNoyoTerminated,
    employeePublicKey: result.employeePublicKey,
  }
}

export function MemberNameCell({
  side,
  noyoEmployeeId,
  fields,
  fetchedMembers,
  fetching,
}: MemberNameCellProps) {
  const nameFromFields = memberNameFromFields(
    fields,
    side === 'sureco' ? 'platform' : 'carrier'
  )
  const [loaded, setLoaded] = useState<LoadedMember | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (fetching === true) {
      setLoading(false)
      return
    }
    if (Array.isArray(fetchedMembers) && fetchedMembers.length > 0) {
      return
    }
    if (typeof nameFromFields === 'string' && nameFromFields.length > 0) {
      return
    }
    if (noyoEmployeeId.length === 0) {
      return
    }

    let cancelled = false
    setLoading(true)
    void fetchPlatformEmployee(noyoEmployeeId)
      .then((result) => {
        if (cancelled) {
          return
        }
        const next = loadedMemberFromResult(side, result)
        if (next != null) {
          setLoaded(next)
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
      setLoading(false)
    }
  }, [fetchedMembers, fetching, nameFromFields, noyoEmployeeId, side])

  const handleGotoEmployee = async (publicKey: string) => {
    if (loading) {
      return
    }

    let employeePublicKey = publicKey
    const tab = window.open('', '_blank')
    setLoading(true)
    try {
      if (employeePublicKey.length === 0) {
        const result = await fetchPlatformEmployee(noyoEmployeeId)
        employeePublicKey = result.employeePublicKey
        const next = loadedMemberFromResult(side, result)
        if (next != null) {
          setLoaded(next)
        }
      }
      if (employeePublicKey.length === 0) {
        tab?.close()
        toast.error('No platform employee id was returned')
        return
      }
      const path = employeeAdminPath(employeePublicKey)
      if (tab === null || tab.closed) {
        window.location.assign(path)
        return
      }
      tab.location.assign(path)
    } catch (err) {
      tab?.close()
      toast.error(`Failed to open employee: ${getErrorMessage(err)}`)
    } finally {
      setLoading(false)
    }
  }

  let members: NoyoPlanMember[] = []
  if (Array.isArray(fetchedMembers) && fetchedMembers.length > 0) {
    members = fetchedMembers
  } else if (loaded != null) {
    members = [loaded]
  } else if (typeof nameFromFields === 'string' && nameFromFields.length > 0) {
    members = [
      {
        name: nameFromFields,
        isTerminated: false,
        employeePublicKey: '',
      },
    ]
  }

  if (members.length > 0) {
    return (
      <Space direction="vertical" size={4}>
        {members.map((member, index) => {
          const hasPublicKey =
            typeof member.employeePublicKey === 'string' &&
            member.employeePublicKey.length > 0
          const usingFetchedMembers =
            Array.isArray(fetchedMembers) && fetchedMembers.length > 0
          const canGoto =
            side === 'sureco' &&
            (hasPublicKey ||
              (usingFetchedMembers === false && noyoEmployeeId.length > 0))
          return (
            <MemberRow
              key={`${member.name}-${index}`}
              member={member}
              canGotoEmployee={canGoto}
              loading={loading}
              onGotoEmployee={() =>
                void handleGotoEmployee(member.employeePublicKey ?? '')
              }
            />
          )
        })}
      </Space>
    )
  }

  if (fetching === true || loading) {
    return <Spin size="small" />
  }

  return <span>—</span>
}
